import React, {
  Suspense, lazy, useEffect, useState,
} from 'react';
import PropTypes from 'prop-types';
import { Route, Routes, useNavigate, Navigate} from 'react-router-dom';
import LoadingModal from '../../components/modal/LoadingModal';
import { sendCustomFullStoryEvent } from '../vendor/fullstory/FullStoryUpdater';
import { getFeatureFlag } from '../../store/helpers/featureFlagHelper';
import { ServiceProvider } from '../../context/ServiceContext';

const PageTitle = lazy(() => import('../../components/page-title/PageTitle'));
const PersonalInfoWithAuthHandle = lazy(() => import('./personal-info/PersonalInfoWithAuthHandle'));

function QuickApp({
  featureToggles,
  flagNames,
  forceLogoutEnabled = false,
  Services,
  subscribeToGenesysChatState,
  unsubscribeToGenesysChatState,
  subscribeToGenesysCobrowseState,
  unsubscribeToGenesysCobrowseState,
  subscribeToGenesysTextState,
  unsubscribeToGenesysTextState,
  location = { search: '' }
}) {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const loading = <LoadingModal />;
  const navigate = useNavigate();

  useEffect(() => {
    const cognitoValidationEnabled = getFeatureFlag(flagNames.COGNITO_VALIDATION_ENABLED);
    if (cognitoValidationEnabled) {
      Services.forgerockService.validateLoggedInUser()
        .then(response => {
          if (response.data.valid === true) {
            setIsLoggedIn(true);
          }
        }).catch(error => {
          sendCustomFullStoryEvent('VALIDATE_USER_SESSION_ERROR', {
            failedMethod: 'validateLoggedInUser',
            error: {
              message: error.message,
              stack: error.stack,
            },
          });
        });
    }
  }, []);

  useEffect(() => {
    if (localStorage.getItem('cognitoTokenExpiresAt')) {
      const expiresAt = localStorage.getItem('cognitoTokenExpiresAt');
      const now = new Date().getTime() / 1000;
      if ((expiresAt - now) > 0) {
        window.location.replace(`${window.__ENV.REACT_APP_BECOME_LOGIN_URL}`);
      }
    }
  }, [localStorage.getItem('cognitoTokenExpiresAt')]);

  // eslint-disable-next-line consistent-return
  useEffect(() => {
    try {
      if (featureToggles[flagNames.GENESYS_CHAT_ENABLED]) {
        const cobrowseEnabled = featureToggles[flagNames.GENESYS_COBROWSE_ENABLED];
        import('../../vendor/genesys_cx/widget_config').then(configModule => {
          if (cobrowseEnabled) {
            configModule.default.setupCobrowse();
          }
        });
        subscribeToGenesysChatState();

        if (cobrowseEnabled) {
          subscribeToGenesysCobrowseState();
          return () => {
            unsubscribeToGenesysChatState();
            unsubscribeToGenesysCobrowseState();
          };
        }
        return () => unsubscribeToGenesysChatState();
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Error configuring support chat:', err);
    }
  }, [featureToggles, flagNames]);

  // eslint-disable-next-line consistent-return
  useEffect(() => {
    try {
      if (featureToggles[flagNames.GENESYS_TEXT_ENABLED]) {
        subscribeToGenesysTextState();
        return () => unsubscribeToGenesysTextState();
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Error configuring support text:', err);
    }
  }, [featureToggles, flagNames]);

  useEffect(() => {
    if (isLoggedIn) {
      navigate('/admissions'); // Redirect to /admissions if logged in
    }
  }, [isLoggedIn, navigate]);

  return (
    <>
      <PageTitle />
      <ServiceProvider services={Services}>
        <Suspense fallback={loading}>
          <Routes>
            <Route 
              path="/" 
              element={<Navigate to={`personal-info${window.location.search}`} replace />} 
            />
            <Route
              path="/personal-info"
              element={
                <PersonalInfoWithAuthHandle
                  forceLogoutEnabled={forceLogoutEnabled}
                  location={window.location}
                />
              }
            />
          </Routes>
        </Suspense>
      </ServiceProvider>
    </>
  );
}

QuickApp.propTypes = {
  location: PropTypes.shape({
    search: PropTypes.string,
  }),
  forceLogoutEnabled: PropTypes.bool,
  featureToggles: PropTypes.shape({}).isRequired,
  flagNames: PropTypes.shape({
    GENESYS_CHAT_ENABLED: PropTypes.string.isRequired,
    GENESYS_COBROWSE_ENABLED: PropTypes.string.isRequired,
    COGNITO_VALIDATION_ENABLED: PropTypes.string.isRequired,
    GENESYS_TEXT_ENABLED: PropTypes.string.isRequired,
  }).isRequired,
  subscribeToGenesysChatState: PropTypes.func.isRequired,
  unsubscribeToGenesysChatState: PropTypes.func.isRequired,
  subscribeToGenesysTextState: PropTypes.func.isRequired,
  unsubscribeToGenesysTextState: PropTypes.func.isRequired,
  subscribeToGenesysCobrowseState: PropTypes.func.isRequired,
  unsubscribeToGenesysCobrowseState: PropTypes.func.isRequired,
  Services: PropTypes.shape({
    forgerockService: PropTypes.shape({
      validateLoggedInUser: PropTypes.func.isRequired,
    }),
  }).isRequired,
};

export default QuickApp;
